import { ref } from 'vue'
import { ElNotification } from 'element-plus'
import type { NotificationHandle } from 'element-plus/lib/components/notification'
import type { UserModule } from '~/types'

const ONE_MINUTE = 60 * 1000
const TEN_MINUTES = 10 * ONE_MINUTE
const TWENTY_MINUTES = 20 * ONE_MINUTE
const STARTUP_JITTER = 30 * 1000
const UPDATE_TIME_KEY = 'pwa-update-time'
const PWA_UPDATE_CLASS = 'pwa-update-notification'

function formatTimeRemaining(milliseconds: number): string {
  if (milliseconds <= 0) return 'now'
  const minutes = Math.floor(milliseconds / 60000)
  const seconds = Math.floor((milliseconds % 60000) / 1000)
  return `${minutes}m ${seconds}s`
}

// https://github.com/antfu/vite-plugin-pwa#automatic-reload-when-new-content-available
export const install: UserModule = ({ isClient, router }) => {
  if (!isClient) return
  const notification = ref<NotificationHandle | null>(null)
  const message = ref('')
  let countdownInterval: number | null = null

  function clearUpdateStateAndCloseNotification() {
    if (countdownInterval !== null) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
    if (notification.value) {
      notification.value.close()
      notification.value = null
    }
    localStorage.removeItem(UPDATE_TIME_KEY)
  }

  function triggerUpdate(updateSW: () => Promise<void>) {
    clearUpdateStateAndCloseNotification()
    updateSW()
  }

  function startRobustUpdateCountdown(updateSW: () => Promise<void>) {
    // check if we already have an update time stored
    let targetTime: number
    const storedUpdateTime = localStorage.getItem(UPDATE_TIME_KEY)
    if (storedUpdateTime) {
      targetTime = parseInt(storedUpdateTime)
    } else {
      targetTime = Date.now() + TEN_MINUTES
      localStorage.setItem(UPDATE_TIME_KEY, targetTime.toString())
    }

    function createOrUpdateNotification() {
      const timeRemaining = targetTime - Date.now()

      if (timeRemaining <= 0) {
        triggerUpdate(updateSW)
        return
      }
      message.value = `Click here to update Dray Dog. Auto-update will occur in ${formatTimeRemaining(timeRemaining)}.`

      if (!notification.value) {
        notification.value = ElNotification({
          title: 'Update Available',
          message: message.value,
          type: 'warning',
          duration: 0,
          onClick: () => triggerUpdate(updateSW),
          showClose: false,
          customClass: PWA_UPDATE_CLASS,
        })
      } else {
        // it seems like this is the only way to update the message
        // using vNode does not work to reactively update the message
        // find the element with the class PWA_UPDATE_CLASS and update the message
        const el = document.querySelector(
          `.${PWA_UPDATE_CLASS} .el-notification__content`
        )
        if (el) el.textContent = message.value
      }
    }

    if (countdownInterval !== null) {
      window.clearInterval(countdownInterval)
    }

    // Update immediately and then every second
    createOrUpdateNotification()
    countdownInterval = window.setInterval(createOrUpdateNotification, 1000)
  }

  router
    .isReady()
    .then(async () => {
      const { registerSW } = await import('virtual:pwa-register')
      // Jitter so that all clients don't update at the same time. Trying to prevent
      // 503's due to a "thundering herd"
      const randomJitter = Math.floor(Math.random() * STARTUP_JITTER)
      const updateSW = registerSW({
        immediate: true,
        onRegistered(registration) {
          if (registration) {
            setInterval(() => {
              registration.update()
            }, ONE_MINUTE + randomJitter)
          }
        },
        onNeedRefresh() {
          // Check if we already have an update time stored
          const storedUpdateTime = localStorage.getItem(UPDATE_TIME_KEY)
          if (storedUpdateTime) {
            const targetTime = parseInt(storedUpdateTime)
            if (Date.now() < targetTime) {
              // Resume countdown if time hasn't expired
              startRobustUpdateCountdown(updateSW)
            } else {
              // Time has expired, trigger update
              triggerUpdate(updateSW)
            }
          } else {
            // evenly spread out the update countdown
            const randomDelay = Math.floor(Math.random() * TWENTY_MINUTES)
            setTimeout(() => {
              startRobustUpdateCountdown(updateSW)
            }, randomDelay)
          }
        },
      })
    })
    .catch(() => {})
}
